<!-- If your content is only a simple string, you can use the content prop: -->
<template>
  <span
    ref="trigger"
    class="base-tooltip-trigger"
    data-pendo-ds="tooltip"
    @mouseleave="hide"
    @mouseover="show"
  >
    <div
      v-show="showing && (content || $slots.content)"
      ref="tooltip"
      class="base-tooltip"
      :style="{
        backgroundColor: backgroundColor,
        color: textColor,
        border: `1px solid ${borderColor}`,
      }"
    >
      <div
        v-if="arrow"
        class="floating-arrow"
        data-popper-arrow
      />
      <slot name="content">
        {{ content }}
      </slot>
    </div>
    <slot
      :hide-tooltip="hide"
      :show-tooltip="show"
    />
  </span>
</template>

<script>
  import { createPopper } from '@popperjs/core'

  const POPPER_POSITIONS = [ "auto", "auto-start", "auto-end", "top", "top-start", "top-end",
                             "bottom", "bottom-start", "bottom-end", "right", "right-start", "right-end",
                             "left", "left-start", "left-end"]

  export default {
    name: "BaseTooltip",
    props: {
      placement: {
        type: String,
        default: "auto",
        validator: function(value) {
          return POPPER_POSITIONS.includes(value)
        },
      },
      arrow: {
        type: Boolean,
        default: true,
      },
      content: {
        type: String,
        default: "",
      },
      backgroundColor: {
        type: String,
        default: "var(--pepper-900)",
      },
      textColor: {
        type: String,
        default: "var(--salt)",
      },
      borderColor: {
        type: String,
        default: "var(--pepper-600)",
      },
      displayCondition: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        showing: false,
        popperInstance: {},
      }
    },
    computed: {
      arrowColor() {
        return this.backgroundColor
      },
    },
    mounted() {
      const trigger = this.$refs.trigger
      const tooltip = this.$refs.tooltip

      this.popperInstance = createPopper(trigger, tooltip, {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 10],
            },
          },
          // event listeners are disabled while tooltip is hidden for performance
          // https://popper.js.org/docs/v2/tutorial/#performance
          { name: 'eventListeners', enabled: false },
          this.arrow && {
            name: 'arrow',
            options: {
              padding: 4,
            },
          },
        ],
      })
    },
    methods: {
      show() {
        if (!this.displayCondition) {
          return
        }
        this.showing = true

        this.popperInstance.setOptions((options) => ({
          ...options,
          modifiers: [
            ...options.modifiers,
            { name: 'eventListeners', enabled: true },
          ],
        }))

        this.popperInstance.update()
      },
      hide() {
        this.showing = false

        this.popperInstance.setOptions((options) => ({
          ...options,
          modifiers: [
            ...options.modifiers,
            { name: 'eventListeners', enabled: false },
          ],
        }))
      },
    },
  }
</script>

<style scoped lang="scss">
.base-tooltip-trigger {
  height: inherit;
  width: inherit;
  font-size: inherit;
}
.base-tooltip {
  position: absolute;
  border-radius: var(--cr-br-small);
  padding: var(--space-1) var(--space-2);
  box-shadow: var(--cr-ds-small);
  font-size: var(--font-size-2);
  font-weight: 300;
  font-family: var(--font-family-primary);
  z-index: 1;
  max-width: 250px;
  text-align: center;
  pointer-events: none;
  white-space: pre-wrap;

  .floating-arrow {
    background-color: transparent;

    &,
    &::before {
      width: 8px;
      height: 8px;
      position: absolute;
      z-index: -1;
    }

    &::before {
      content: '';
      background-color: v-bind(arrowColor);
      border-radius: 1px;
      transform: rotate(45deg);
    }
  }

  &[data-popper-placement^='top'] > .floating-arrow {
    bottom: -4px;

    &::before {
      left: 0;
    }
  }

  &[data-popper-placement^='bottom'] > .floating-arrow {
    top: -4px;

    &::before {
      left: 0;
    }
  }

  &[data-popper-placement^='left'] > .floating-arrow {
    right: 0;
  }

  &[data-popper-placement^='right'] > .floating-arrow {
    left: -8px;
  }
}
</style>
